// AppRendicionReactTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from "react-table";
import { getViajes } from "../helpers/rutaViajes";
import Table from "./TableContainer";
import { generarPDFviajeConDetalles } from "../helpers/pdfViajes";
import ModalFormViaje from "../components/ModalFormViaje";
import ModalFormViajePago from "../components/ModalFormViajePago";
import { getComandas } from "../helpers/rutaComandas";

import "../css/tablecomandas.css";

const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }
  table { border: 1px solid black; border-spacing: 0; font-size: 13px; }
  th, td { padding: 0.5rem; border-bottom: 1px solid black; border-right: 1px solid black; }
  th { background-color: #778899; position: sticky; top: 0; z-index: 2; text-align: center; font-size: 12px; }
  td { background-color: white; }
  .pagination { padding: 0.5rem; background-color: #778899; font-size: 15px; font-weight: bold; }
`;

const InformeRendicionesHeader = styled.div`
  display: flex;
  align-items: center;

  min-height: 78px;

  margin-bottom: 12px;

  padding: 14px 18px;

  border: 1px solid #d5e0ee;
  border-radius: 14px;

  background:
    linear-gradient(
      120deg,
      #ffffff 0%,
      #f8fafc 65%,
      #edf2f8 100%
    );

  box-shadow:
    0 3px 14px
    rgba(15, 23, 42, 0.035);

  .header-icon {
    width: 46px;
    height: 46px;

    flex: 0 0 46px;

    display: flex;
    align-items: center;
    justify-content: center;

    margin-right: 13px;

    border-radius: 11px;

    background: #eaf0f8;

    color: #496b99;

    font-size: 19px;
  }

  .header-text span {
    display: block;

    margin-bottom: 2px;

    color: #5577a4;

    font-size: 9px;
    font-weight: 700;

    letter-spacing: 1.7px;
  }

  .header-text h3 {
    margin: 0 0 3px 0;

    color: #18243a;

    font-size: 20px;
    font-weight: 700;
  }

  .header-text p {
    margin: 0;

    color: #748196;

    font-size: 10px;
  }

  @media (max-width: 768px) {
    padding: 12px;

    .header-icon {
      width: 42px;
      height: 42px;

      flex-basis: 42px;
    }

    .header-text h3 {
      font-size: 17px;
    }
  }
`;

const normalizarNroViaje = (n) =>
  String(n ?? "").trim().replace(",", ".");

const baseNroViaje = (n) =>
  normalizarNroViaje(n).split(".")[0];

const perteneceAlViaje = (nroViajeComanda, nroViajeViaje) =>
  baseNroViaje(nroViajeComanda) === baseNroViaje(nroViajeViaje);

const extraerListaComandas = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.comandas)) return resp.comandas;
  if (Array.isArray(resp?.data)) return resp.data;
  return [];
};

export default function AppRendicionReactTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viajeSeleccionado, setViajeSeleccionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showModalPago, setShowModalPago] = useState(false);

  const fetchData = useCallback(async () => {
    const resp = await getViajes();
    if (!resp || !Array.isArray(resp)) {
      console.error("Error al obtener viajes", resp);
      setData([]);
    } else {
      setData(resp);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleClosePago = () => { setShowModalPago(false); fetchData(); };
  const handleClose     = () => { setShowModal(false);     fetchData(); };

  const handleGenerarPDF = useCallback(async (viaje) => {
    try {
      const respComandas = await getComandas();
      const todasLasComandas = extraerListaComandas(respComandas);

      const comandasRelacionadas = todasLasComandas
        .filter((c) =>
          perteneceAlViaje(c.nroviaje || c.nroViaje, viaje.nroViaje)
        )
        .map((c) => ({
          ...c,
          pactado:
            Number(
              c.pactadoter ??
              c.pactadoTercero ??
              c.pactado ??
              c.total ??
              0
            ) || 0,
        }));

      generarPDFviajeConDetalles(viaje, comandasRelacionadas);
    } catch (error) {
      console.error("Error al generar PDF con comandas relacionadas:", error);
      generarPDFviajeConDetalles(viaje, []);
    }
  }, []);

  const calcularResumen = (v) => {
    const kmTableros = (v.combustible || []).map(c => Number(c.kmTablero)).filter(km => !isNaN(km));
    const totalKm = kmTableros.length >= 2 ? Math.max(...kmTableros) - Math.min(...kmTableros) : 0;
    const tarifa = Number(v.tarifaPorKm || 130);
    const totalLitros = (v.combustible || []).reduce((s, c) => s + Number(c.litros), 0);
    const totalCombustible = (v.combustible || []).reduce((s, c) => s + Number(c.monto), 0);
    const totalViaticos = (v.viaticos || []).reduce((s, x) => s + Number(x.monto), 0);
    const totalAdelantos = (v.adelantos || []).reduce((s, a) => s + Number(a.monto), 0);
    const totalGastos = (v.gastos || []).reduce((s, g) => s + Number(g.monto), 0);
    const liquidacionFinal = (totalKm * tarifa + totalViaticos) - (totalAdelantos - totalGastos);
    const promedioConsumo = totalLitros > 0 ? (totalKm / totalLitros).toFixed(2) : "-";
    return { totalKm, totalLitros, promedioConsumo, totalCombustible, totalViaticos, totalAdelantos, totalGastos, liquidacionFinal };
  };

  const columns = useMemo(() => [
    {
      id: "pdf",
      Header: "PDF",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--pdf"
          title="Descargar PDF"
          onClick={() => handleGenerarPDF(row.original)}
        >
          <i className="fa fa-file-pdf-o" aria-hidden="true" />
        </button>
      ),
    },
    {
      id: "editar",
      Header: "Edita",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--edit"
          title="Editar viaje"
          onClick={() => { setViajeSeleccionado(row.original); setShowModal(true); }}
        >
          <i className="fa fa-pencil" aria-hidden="true" />
        </button>
      ),
    },
    {
      id: "pagar",
      Header: "Paga",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--pay"
          title="Registrar pago"
          onClick={() => { setViajeSeleccionado(row.original); setShowModalPago(true); }}
        >
          <i className="fa fa-money" aria-hidden="true" />
        </button>
      ),
    },

    { Header: "Nro Vje", accessor: "nroViaje", width: 60,
      Cell: ({ value }) => <div style={{textAlign:'right'}}>{value}</div> },

    { Header: "Chofer", accessor: v => v.choferId?.nombre || "-", id: "chofer", width: 120 },
    { Header: "Camión", accessor: v => v.camionId ? `${v.camionId.camion || ""} (${v.camionId.patente || ""})` : "-", id: "camion", width: 120 },

    {
      Header: "Fecha Viaje",
      accessor: r => (r.comandaId?.fecha ? new Date(r.comandaId.fecha) : null),
      id: "fechaComanda",
      width: 110,
      Cell: ({ value }) =>
        value instanceof Date
          ? value.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
          : ""
    },
    {
      Header: "KM",
      accessor: (v) => calcularResumen(v).totalKm,
      id: "km",
      Cell: ({ value }) => (
        <div style={{ textAlign: 'right' }}>
          {value}
        </div>
      ),
      width: 80,
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.km || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
            {total.toLocaleString("es-AR")}
          </div>
        );
      }
    },
    {
      Header: "Litros",
      accessor: (v) => calcularResumen(v).totalLitros,
      id: "litros",
      Cell: ({ value }) => (
        <div style={{ textAlign: 'right' }}>
          {value}
        </div>
      ),
      width: 80,
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.litros || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
            {total.toLocaleString("es-AR")}
          </div>
        );
      }
    },
    {
      Header: "Prom Cons",
      accessor: (v) => calcularResumen(v).promedioConsumo,
      id: "promedioConsumo",
      Cell: ({ value }) => (
        <div style={{ textAlign: 'right' }}>
          {value}
        </div>
      ),
      width: 80,
      Footer: info => {
        const totalKm = info.rows.reduce((sum, row) => sum + (row.values.km || 0), 0);
        const totalLitros = info.rows.reduce((sum, row) => sum + (row.values.litros || 0), 0);
        const promedio = totalLitros > 0 ? (totalKm / totalLitros).toFixed(2) : "-";
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
            {promedio}
          </div>
        );
      }
    },
    {
      Header: "Combustible",
      accessor: (v) => calcularResumen(v).totalCombustible,
      id: "combustible",
      width: 100,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {value?.toLocaleString("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 0,
          })}
        </div>
      ),
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.combustible || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold', color: 'green' }}>
            ${total.toLocaleString("es-AR")}
          </div>
        );
      }
    },
    {
      Header: "Viáticos",
      accessor: (v) => calcularResumen(v).totalViaticos,
      id: "viaticos",
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {value?.toLocaleString("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 0,
          })}
        </div>
      ),
      width: 100,
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.viaticos || 0), 0);
        return (
          <div style={{ textAlign: "right", fontWeight: "bold", color: "green" }}>
            {total.toLocaleString("es-AR", {
              style: "currency",
              currency: "ARS",
              minimumFractionDigits: 0,
            })}
          </div>
        );
      }
    },
    {
      Header: "Adelantos",
      accessor: (v) => calcularResumen(v).totalAdelantos,
      id: "adelantos",
      Cell: ({ value }) => (
        <div style={{ textAlign: 'right' }}>
          {value?.toLocaleString("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 0,
          })}
        </div>
      ),
      width: 100,
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.adelantos || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold', color: 'red' }}>
            ${total.toLocaleString("es-AR")}
          </div>
        );
      }
    },
    {
      Header: "Gastos",
      accessor: (v) => calcularResumen(v).totalGastos,
      id: "gastos",
      Cell: ({ value }) => (
        <div style={{ textAlign: 'right' }}>
          {value?.toLocaleString("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 0,
          })}
        </div>
      ),
      width: 100,
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.gastos || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold', color: 'red' }}>
            ${total.toLocaleString("es-AR")}
          </div>
        );
      }
    },
    {
      Header: "💰 Total a Cobrar",
      accessor: (v) => calcularResumen(v).liquidacionFinal,
      id: "liquidacionFinal",
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {value?.toLocaleString("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 0,
          })}
        </div>
      ),
      width: 130,
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.liquidacionFinal || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold', color: 'blue' }}>
            ${total.toLocaleString("es-AR")}
          </div>
        );
      }
    },
    {
      Header: "Pagado",
      accessor: "montoPagado",
      id: "montoPagado",
      width: 100,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {value?.toLocaleString("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 0,
          })}
        </div>
      ),
      Footer: (info) => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.montoPagado || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold', color: 'green' }}>
            ${total.toLocaleString("es-AR")}
          </div>
        );
      }
    },

    {
      Header: "Fecha Pago",
      accessor: (v) => v.fechaPago ? new Date(v.fechaPago) : "",
      id: "fechaPago",
      width: 100,
      Cell: ({ value }) =>
        value instanceof Date
          ? value.toLocaleDateString("es-AR")
          : "-",
    },
    {
      Header: "Estado Pago",
      accessor: "estadoPago",
      id: "estadoPago",
      width: 100,
      Cell: ({ value }) => (
        <span style={{ color: value === "Pagado" ? "green" : "orange", fontWeight: "bold" }}>
          {value}
        </span>
      ),
    },
    {
      Header: "Método Pago",
      accessor: (v) => v.metodoPago?.metodo || "-", // aseguro string o guion
      id: "metodoPago",
      width: 100,
      Cell: ({ value }) => (
        <div style={{ textAlign: '', fontWeight: 'bold' }}>
          {value}
        </div>
      ),
    },

    ],
    [handleGenerarPDF]
  );

  return (
    <div className="container mt-4">

      <InformeRendicionesHeader>
        <div className="header-icon">
          <i
            className="fa fa-file-text-o"
            aria-hidden="true"
          />
        </div>

        <div className="header-text">
          <span>
            INFORMES · LIQUIDACIONES
          </span>

          <h3>
            Rendición de Viajes
          </h3>

          <p>
            Liquidación de kilómetros, combustible,
            viáticos, adelantos, gastos y pagos por viaje.
          </p>
        </div>
      </InformeRendicionesHeader>

      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={data}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {showModal && (
        <ModalFormViaje
          show={showModal}
          handleClose={handleClose}
          viaje={viajeSeleccionado}
        />
      )}

      {showModalPago && (
        <ModalFormViajePago
          show={showModalPago}
          handleClose={handleClosePago}
          viaje={viajeSeleccionado}
        />
      )}
    </div>
  );
}